import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  useTheme,
  Chip,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import StatsCard from './StatsCard';

interface BrandDetailViewProps {
  brand: string;
  onBack: () => void;
}

const brandColors: Record<string, string> = {
  "Happy's Pizza": '#FF6B6B',
  "Savvy Sliders": '#4ECDC4',
  "BurgerFi": '#AF2326',
  "Fat Boys Pizza": '#FFD93D',
};

const BrandDetailView: React.FC<BrandDetailViewProps> = ({ brand, onBack }) => {
  const theme = useTheme();

  // Generate realistic demo data for the selected brand
  const salesTrendData = useMemo(() => {
    const baseDaily = brand === "Happy's Pizza" ? 8500 : 
                     brand === "Savvy Sliders" ? 6200 :
                     brand === "BurgerFi" ? 7800 : 5900;
    
    return Array.from({ length: 30 }, (_, i) => {
      const date = format(subDays(new Date(), 29 - i), 'MM/dd');
      const variance = (Math.random() - 0.5) * 0.3;
      const sales = Math.round(baseDaily * (1 + variance));
      const lastYearSales = Math.round(sales * 0.88 * (1 + variance * 0.7));
      
      return {
        date,
        sales,
        lastYearSales,
        orders: Math.round(sales / 32),
        avgOrder: 32
      };
    });
  }, [brand]);

  const hourlyData = useMemo(() => {
    const peakHours = [11, 12, 17, 18, 19];
    const storeMultiplier = brand === "Happy's Pizza" ? 25 : 
                           brand === "Savvy Sliders" ? 18 :
                           brand === "BurgerFi" ? 22 : 16;
    
    return Array.from({ length: 24 }, (_, hour) => {
      const isPeak = peakHours.includes(hour);
      const baseHourly = storeMultiplier * (isPeak ? 180 : hour >= 6 && hour <= 22 ? 90 : 20);
      const variance = (Math.random() - 0.5) * 0.25;
      const sales = Math.round(baseHourly * (1 + variance));
      
      return { 
        hour, 
        sales, 
        orders: Math.round(sales / 32),
        time: `${hour.toString().padStart(2, '0')}:00`
      };
    });
  }, [brand]);

  const categoryData = useMemo(() => {
    if (brand === "Happy's Pizza") {
      return [
        { name: 'Pizza', value: 55, color: '#FF6B6B' },
        { name: 'Wings', value: 20, color: '#FF8E8E' },
        { name: 'Subs', value: 15, color: '#FFB1B1' },
        { name: 'Beverages', value: 10, color: '#FFD4D4' }
      ];
    } else if (brand === "Savvy Sliders") {
      return [
        { name: 'Sliders', value: 45, color: '#4ECDC4' },
        { name: 'Sides', value: 25, color: '#7ED9D5' },
        { name: 'Beverages', value: 18, color: '#AEE5E2' },
        { name: 'Desserts', value: 12, color: '#DEF1EF' }
      ];
    } else if (brand === "BurgerFi") {
      return [
        { name: 'Burgers', value: 50, color: '#AF2326' },
        { name: 'Fries', value: 22, color: '#C94144' },
        { name: 'Shakes', value: 16, color: '#E35E61' },
        { name: 'Beverages', value: 12, color: '#ED7B7E' }
      ];
    } else {
      return [
        { name: 'Pizza', value: 60, color: '#FFD93D' },
        { name: 'Wings', value: 18, color: '#FFE366' },
        { name: 'Sides', value: 14, color: '#FFED8F' },
        { name: 'Beverages', value: 8, color: '#FFF7B8' }
      ];
    }
  }, [brand]);

  const storePerformanceData = useMemo(() => {
    const storeCount = brand === "Happy's Pizza" ? 25 : 
                      brand === "Savvy Sliders" ? 18 :
                      brand === "BurgerFi" ? 22 : 16;
    
    return Array.from({ length: Math.min(storeCount, 10) }, (_, i) => {
      const storeId = `${brand.slice(0, 3).toUpperCase()}-${(i + 1).toString().padStart(3, '0')}`;
      const baseSales = Math.round((Math.random() * 2000 + 800));
      
      return {
        store: storeId,
        sales: baseSales,
        orders: Math.round(baseSales / 32),
        growth: Math.round((Math.random() - 0.3) * 25)
      };
    }).sort((a, b) => b.sales - a.sales);
  }, [brand]);

  // Brand metrics
  const brandMetrics = useMemo(() => {
    const baseSales = brand === "Happy's Pizza" ? 185000 : 
                     brand === "Savvy Sliders" ? 142000 :
                     brand === "BurgerFi" ? 168000 : 128000;
    
    return {
      todaySales: baseSales,
      todayOrders: Math.round(baseSales / 32),
      avgOrderValue: 32,
      mtdSales: baseSales * 22,
      growth: Math.round((Math.random() - 0.2) * 20)
    };
  }, [brand]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            ...glassStyle,
            p: 2,
            borderRadius: '12px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontWeight: 600 }}
            >
              {entry.name}: ${entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Header */}
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              Back to Overview
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
              {brand} Analytics
            </Typography>
            <Chip
              icon={brandMetrics.growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${Math.abs(brandMetrics.growth)}% ${brandMetrics.growth >= 0 ? 'Growth' : 'Decline'}`}
              sx={{
                backgroundColor: brandMetrics.growth >= 0 ? 'success.main' : 'error.main',
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        </Box>
      </motion.div>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Today's Revenue"
            value={brandMetrics.todaySales}
            prefix="$"
            change={brandMetrics.growth}
            changeText="vs yesterday"
            icon={<TrendingUpIcon />}
            delay={0}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Orders"
            value={brandMetrics.todayOrders}
            change={brandMetrics.growth * 0.8}
            changeText="vs yesterday"
            icon={<TrendingUpIcon />}
            delay={0.1}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Average Order"
            value={brandMetrics.avgOrderValue}
            prefix="$"
            icon={<TrendingUpIcon />}
            delay={0.2}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="MTD Revenue"
            value={brandMetrics.mtdSales}
            prefix="$"
            change={brandMetrics.growth * 1.2}
            changeText="vs last month"
            icon={<TrendingUpIcon />}
            delay={0.3}
            color="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Sales Trend Chart */}
        <Grid item xs={12} lg={8}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 450 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {brand} Sales Trend (30 Days)
              </Typography>
              
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={salesTrendData}>
                  <defs>
                    <linearGradient id={`${brand}Gradient`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brandColors[brand]} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={brandColors[brand]} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="date" stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <YAxis stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="lastYearSales"
                    stroke="#666"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    fillOpacity={0.1}
                    fill="#666"
                    name="Last Year Sales"
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke={brandColors[brand]}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#${brand}Gradient)`}
                    name="Current Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* Category Breakdown */}
        <Grid item xs={12} lg={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 450 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Product Mix
              </Typography>
              
              <ResponsiveContainer width="100%" height="60%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box sx={{ mt: 2 }}>
                {categoryData.map((category) => (
                  <Box
                    key={category.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '3px',
                          backgroundColor: category.color,
                        }}
                      />
                      <Typography variant="body2">{category.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Hourly Performance */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.6 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Hourly Performance - {brand}
              </Typography>

              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis stroke="#A0AEC0" style={{ fontSize: '0.75rem' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="sales" 
                    fill={brandColors[brand]} 
                    radius={[8, 8, 0, 0]} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* Top Performing Stores */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.7 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Top Performing {brand} Locations
              </Typography>
              
              <Grid container spacing={2}>
                {storePerformanceData.map((store, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={store.store}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {store.store}
                      </Typography>
                      <Typography variant="h6" sx={{ color: brandColors[brand] }}>
                        ${store.sales.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {store.orders} orders
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {store.growth >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{ 
                            color: store.growth >= 0 ? 'success.main' : 'error.main',
                            ml: 0.5 
                          }}
                        >
                          {Math.abs(store.growth)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrandDetailView;